import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth/AuthContext.tsx';
import type { UserRole } from './types.ts';
import { dashboardPathFor } from './auth/dashboardPath.ts';
import { ImpersonationBanner } from './components/ImpersonationBanner.tsx';
import { LandingPage } from './pages/LandingPage.tsx';
import { PreviewPage } from './pages/PreviewPage.tsx';
import { LoginPage } from './pages/LoginPage.tsx';
import { RegisterPage } from './pages/RegisterPage.tsx';
import { ExtraDashboard } from './pages/extra/ExtraDashboard.tsx';
import { MissionDetail } from './pages/extra/MissionDetail.tsx';
import { Confirmation } from './pages/extra/Confirmation.tsx';
import { ExtraProfileSelf } from './pages/extra/ExtraProfileSelf.tsx';
import { ExtraBusinessLayout } from './pages/extra/business/ExtraBusinessLayout.tsx';
import { BusinessHome } from './pages/extra/business/BusinessHome.tsx';
import { Invoices } from './pages/extra/business/Invoices.tsx';
import { Clients } from './pages/extra/business/Clients.tsx';
import { Revenues } from './pages/extra/business/Revenues.tsx';
import { Urssaf } from './pages/extra/business/Urssaf.tsx';
import { BillingProfile } from './pages/extra/business/BillingProfile.tsx';
import { ExtraInvoicePage } from './pages/extra/ExtraInvoicePage.tsx';
import { AdminCompliance } from './pages/admin/AdminCompliance.tsx';
import { AdminVerifications } from './pages/admin/AdminVerifications.tsx';
import { Verifications } from './pages/extra/business/Verifications.tsx';
import { ConversationPage, MessagesPage } from './pages/messages/MessagesPage.tsx';
import { AdminMessaging } from './pages/admin/AdminMessaging.tsx';
import { ReferencePage } from './pages/ReferencePage.tsx';
import { ProProfile } from './pages/extra/ProProfile.tsx';
import { AdminCatalog } from './pages/admin/AdminCatalog.tsx';
import { AdminModeration } from './pages/admin/AdminModeration.tsx';
import { AdminRatings } from './pages/admin/AdminRatings.tsx';
import { ExtraFeedback } from './pages/extra/ExtraFeedback.tsx';
import { ExtraPayouts } from './pages/extra/ExtraPayouts.tsx';
import { OrgCard } from './pages/resto/OrgCard.tsx';
import { RestoDashboard } from './pages/resto/RestoDashboard.tsx';
import { NewMission } from './pages/resto/NewMission.tsx';
import { RestoMissionDetail } from './pages/resto/RestoMissionDetail.tsx';
import { SearchExtras } from './pages/resto/SearchExtras.tsx';
import { ExtraProfileView } from './pages/resto/ExtraProfileView.tsx';
import { Payment } from './pages/resto/Payment.tsx';
import { AdminLayout } from './pages/admin/AdminLayout.tsx';
import { AdminDashboard } from './pages/admin/AdminDashboard.tsx';
import { AdminUsers } from './pages/admin/AdminUsers.tsx';
import { AdminMissions } from './pages/admin/AdminMissions.tsx';
import { AdminReferrers } from './pages/admin/AdminReferrers.tsx';
import { AdminSettings } from './pages/admin/AdminSettings.tsx';
import { AdminAudit } from './pages/admin/AdminAudit.tsx';
import { ReferrerDashboard } from './pages/referrer/ReferrerDashboard.tsx';
import { ReferrerInvoicePage } from './pages/referrer/ReferrerInvoicePage.tsx';
import { AdminInvoicePage } from './pages/admin/AdminInvoicePage.tsx';
import { ActivatePage } from './pages/ActivatePage.tsx';
import { AppearanceEditor } from './pages/admin/AppearanceEditor.tsx';

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <LandingPage />;
  return <Navigate to={dashboardPathFor(user.role)} replace />;
}

function RequireRole({ roles, children }: { roles: UserRole[]; children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to={dashboardPathFor(user.role)} replace />;
  return <>{children}</>;
}

const EXTRA_ROLES: UserRole[] = ['EXTRA'];
const ORG_ROLES: UserRole[] = ['ORG_OWNER', 'ORG_MANAGER'];
const ADMIN_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN_SUPPORT'];
const SUPER_ROLES: UserRole[] = ['SUPER_ADMIN'];
const REFERRER_ROLES: UserRole[] = ['APPORTEUR'];

export default function App() {
  return (
    <>
    <ImpersonationBanner />
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/preview" element={<PreviewPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/activate" element={<ActivatePage />} />
      <Route path="/reference/:token" element={<ReferencePage />} />

      <Route path="/extra" element={<RequireRole roles={EXTRA_ROLES}><ExtraDashboard /></RequireRole>} />
      <Route path="/extra/paiements" element={<RequireRole roles={EXTRA_ROLES}><ExtraPayouts /></RequireRole>} />
      <Route path="/extra/avis" element={<RequireRole roles={EXTRA_ROLES}><ExtraFeedback /></RequireRole>} />
      <Route path="/extra/profil-pro" element={<RequireRole roles={EXTRA_ROLES}><ProProfile /></RequireRole>} />
      <Route path="/extra/profile" element={<RequireRole roles={EXTRA_ROLES}><ExtraProfileSelf /></RequireRole>} />
      <Route path="/extra/activite" element={<RequireRole roles={EXTRA_ROLES}><ExtraBusinessLayout /></RequireRole>}>
        <Route index element={<BusinessHome />} />
        <Route path="factures" element={<Invoices />} />
        <Route path="clients" element={<Clients />} />
        <Route path="recettes" element={<Revenues />} />
        <Route path="urssaf" element={<Urssaf />} />
        <Route path="verifications" element={<Verifications />} />
        <Route path="facturation" element={<BillingProfile />} />
      </Route>
 
      <Route path="/extra/messages" element={<RequireRole roles={EXTRA_ROLES}><MessagesPage area="extra" /></RequireRole>} />
      <Route path="/extra/messages/:id" element={<RequireRole roles={EXTRA_ROLES}><ConversationPage area="extra" /></RequireRole>} />
      <Route path="/extra/factures/:id" element={<RequireRole roles={EXTRA_ROLES}><ExtraInvoicePage /></RequireRole>} />
      <Route path="/extra/missions/:id" element={<RequireRole roles={EXTRA_ROLES}><MissionDetail /></RequireRole>} />
      <Route path="/extra/missions/:id/confirmation" element={<RequireRole roles={EXTRA_ROLES}><Confirmation /></RequireRole>} />

      <Route path="/resto" element={<RequireRole roles={ORG_ROLES}><RestoDashboard /></RequireRole>} />
      <Route path="/resto/missions/new" element={<RequireRole roles={ORG_ROLES}><NewMission /></RequireRole>} />
      <Route path="/resto/missions/:id" element={<RequireRole roles={ORG_ROLES}><RestoMissionDetail /></RequireRole>} />
      <Route path="/resto/missions/:id/search" element={<RequireRole roles={ORG_ROLES}><SearchExtras /></RequireRole>} />
      <Route path="/resto/missions/:id/payment" element={<RequireRole roles={ORG_ROLES}><Payment /></RequireRole>} />
      <Route path="/resto/messages" element={<RequireRole roles={ORG_ROLES}><MessagesPage area="resto" /></RequireRole>} />
      <Route path="/resto/messages/:id" element={<RequireRole roles={ORG_ROLES}><ConversationPage area="resto" /></RequireRole>} />
      <Route path="/resto/carte" element={<RequireRole roles={ORG_ROLES}><OrgCard /></RequireRole>} />
      <Route path="/resto/extras" element={<RequireRole roles={ORG_ROLES}><SearchExtras /></RequireRole>} />
      <Route path="/resto/extras/:id" element={<RequireRole roles={ORG_ROLES}><ExtraProfileView /></RequireRole>} />

      <Route path="/apporteur" element={<RequireRole roles={REFERRER_ROLES}><ReferrerDashboard /></RequireRole>} />
      <Route path="/apporteur/factures/:id" element={<RequireRole roles={REFERRER_ROLES}><ReferrerInvoicePage /></RequireRole>} />

      <Route path="/admin" element={<RequireRole roles={ADMIN_ROLES}><AdminLayout /></RequireRole>}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="missions" element={<AdminMissions />} />
        <Route path="referrers" element={<RequireRole roles={SUPER_ROLES}><AdminReferrers /></RequireRole>} />
        <Route path="factures/:id" element={<RequireRole roles={SUPER_ROLES}><AdminInvoicePage /></RequireRole>} />
        <Route path="verifications" element={<RequireRole roles={SUPER_ROLES}><AdminVerifications /></RequireRole>} />
        <Route path="moderation" element={<AdminModeration />} />
        <Route path="messagerie" element={<AdminMessaging />} />
        <Route path="notation" element={<RequireRole roles={SUPER_ROLES}><AdminRatings /></RequireRole>} />
        <Route path="referentiel" element={<RequireRole roles={SUPER_ROLES}><AdminCatalog /></RequireRole>} />
        <Route path="conformite" element={<RequireRole roles={SUPER_ROLES}><AdminCompliance /></RequireRole>} />
        <Route path="settings" element={<RequireRole roles={SUPER_ROLES}><AdminSettings /></RequireRole>} />
        <Route path="audit" element={<AdminAudit />} />
      </Route>
      <Route path="/admin/appearance" element={<RequireRole roles={ADMIN_ROLES}><AppearanceEditor /></RequireRole>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
